import { memo, useCallback } from 'react';

import { preserveWorkspaceAgentComposerFocus } from '~/components/v2/components/workspace/workspace-agent-panel/utils/preserveWorkspaceAgentComposerFocus';
import { type Skill } from '~/hooks/queries/skills/usePrivateSkillsList';

export type WorkspaceAgentActionsMenuSkillRowLayout = 'catalog' | 'search';

export type WorkspaceAgentActionsMenuSkillRowProps = {
  id?: string;
  layout: WorkspaceAgentActionsMenuSkillRowLayout;
  onSelect: (skill: Skill) => void;
  skill: Skill;
};

const formatSkillSlashCommandLabel = (slashCommand: string): string =>
  slashCommand.startsWith('/') ? slashCommand : `/${slashCommand}`;

/**
 * Saved-skills list row — matches Strato `SkillCatalogRow` / `SkillSearchRow` in
 * `air-agent-workspace-prototype` (icon tile omitted until skill icons ship).
 */
export const WorkspaceAgentActionsMenuSkillRow = memo(
  ({ id, layout, onSelect, skill }: WorkspaceAgentActionsMenuSkillRowProps) => {
    const handleClick = useCallback(() => onSelect(skill), [onSelect, skill]);
    const commandLabel = formatSkillSlashCommandLabel(skill.slashCommand);
    const description = skill.description.trim();

    if (layout === 'search') {
      return (
        <button
          className="flex w-full items-center gap-2 rounded-6 px-2 py-2 text-left ads:text-primary text-12 text-gray-12 leading-4 ads:hover:bg-hover-subtle hover:bg-gray-4"
          data-skill-id={skill.id}
          data-testid="WORKSPACE_AGENT_ACTIONS_MENU_SKILL_ROW"
          id={id}
          onClick={handleClick}
          onMouseDown={preserveWorkspaceAgentComposerFocus}
          type="button"
        >
          <span className="min-w-0 flex-1 truncate font-medium">{skill.name}</span>
          <span
            className="shrink-0 font-mono ads:text-metadata text-10 text-gray-9 leading-4"
            data-testid="WORKSPACE_AGENT_ACTIONS_MENU_SKILL_ROW_SLASH_COMMAND"
          >
            {commandLabel}
          </span>
        </button>
      );
    }

    return (
      <button
        className="flex w-full items-start rounded-6 p-1.5 text-left ads:hover:bg-hover-subtle hover:bg-gray-4"
        data-skill-id={skill.id}
        data-testid="WORKSPACE_AGENT_ACTIONS_MENU_SKILL_ROW"
        id={id}
        onClick={handleClick}
        onMouseDown={preserveWorkspaceAgentComposerFocus}
        type="button"
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className="min-w-0 flex-1 truncate font-medium ads:text-primary text-12 text-gray-12 leading-4">
              {skill.name}
            </span>
            <span
              className="shrink-0 font-mono ads:text-metadata text-10 text-gray-9 leading-4"
              data-testid="WORKSPACE_AGENT_ACTIONS_MENU_SKILL_ROW_SLASH_COMMAND"
            >
              {commandLabel}
            </span>
          </span>
          {description ? (
            <span className="mt-1 block truncate ads:text-metadata text-12 text-gray-10 leading-4">{description}</span>
          ) : null}
        </span>
      </button>
    );
  },
);

WorkspaceAgentActionsMenuSkillRow.displayName = 'WorkspaceAgentActionsMenuSkillRow';
